import type { FunctionComponent, MouseEventHandler } from 'react';
import { useCallback, useRef } from 'react';

import { forTemplate } from '@trello/i18n';
import { Button } from '@trello/nachos/button';
import { TemplateCardIcon } from '@trello/nachos/icons/template-card';
import { Popover, usePopover } from '@trello/nachos/popover';
import { Tooltip } from '@trello/nachos/tooltip';
import { CardTemplateTestIds } from '@trello/test-ids';

import { mergeRefs } from 'app/src/mergeRefs';
import type { CreateCardFromTemplateMutationResult } from './CreateCardFromTemplateMutation.generated';
import { LazyCardTemplatesPopover } from './LazyCardTemplatesPopover';

import styles from './CardTemplatesButton.less';

const listFormat = forTemplate('list');

type CopyCard = NonNullable<
  NonNullable<CreateCardFromTemplateMutationResult['data']>['copyCard']
>;

export interface CardTemplatesButtonProps {
  idList: string;
  onCardCreated: (card: CopyCard) => void;
}

enum Screen {
  CardTemplates,
  CreateCardFromTemplate,
  ConfirmDelete,
}

export const CardTemplatesButton: FunctionComponent<
  CardTemplatesButtonProps
> = ({ idList, onCardCreated }) => {
  const { toggle, hide, triggerRef, popoverProps, push, pop } =
    usePopover<HTMLButtonElement>({
      initialScreen: Screen.CardTemplates,
    });

  const onClickTooltipRef = useRef<MouseEventHandler<HTMLButtonElement> | null>(
    null,
  );

  const onClickButton = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      toggle();
      onClickTooltipRef.current?.(event);
    },
    [toggle],
  );

  return (
    <>
      <Tooltip
        content={listFormat('create-from-template')}
        delay={100}
        hideTooltipOnMouseDown
      >
        {({ onClick: onClickTooltip, ref: tooltipRef, ...tooltipProps }) => {
          onClickTooltipRef.current = onClickTooltip;
          const mergedRefs = mergeRefs(triggerRef, tooltipRef);
          return (
            <Button
              appearance="subtle"
              iconBefore={
                <TemplateCardIcon color="currentColor" size="small" />
              }
              className={styles.cardTemplateButton}
              data-testid={CardTemplateTestIds.CardTemplateListButton}
              ref={mergedRefs as (node: HTMLButtonElement | null) => void}
              onClick={onClickButton}
              aria-label={listFormat('create-from-template')}
              {...tooltipProps}
            />
          );
        }}
      </Tooltip>
      <Popover {...popoverProps} dangerous_className={styles.popover}>
        <LazyCardTemplatesPopover
          idList={idList}
          hide={hide}
          push={push}
          pop={pop}
          onCardCreated={onCardCreated}
        />
      </Popover>
    </>
  );
};
